import { existsSync, readFileSync, statSync } from "node:fs";
import { createRequire } from "node:module";
import { basename, dirname, join } from "node:path";

import { isRecord } from "./fs.js";
import type { RawEvent, RawEventKind } from "./work-graph-investigation.js";

interface SqliteStatement {
  all(...params: unknown[]): Record<string, unknown>[];
}

interface SqliteDatabase {
  close(): void;
  prepare(sql: string): SqliteStatement;
}

interface SqliteModule {
  DatabaseSync: new (path: string, options?: { readOnly?: boolean }) => SqliteDatabase;
}

export interface AiClientSqliteEvent {
  kind: RawEventKind;
  rawRowId: string;
  role: RawEvent["role"];
  sessionId: string;
  text: string;
  timestamp: string;
  toolName?: string;
  toolStatus?: string;
}

export interface AiClientSqliteStoreResult {
  events: AiClientSqliteEvent[];
  notes: string[];
  readable: boolean;
  searchedSessions: number;
}

function sqliteModule(): SqliteModule | null {
  try {
    return createRequire(import.meta.url)("node:sqlite") as SqliteModule;
  } catch {
    return null;
  }
}

function parseObject(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function textField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function isoFromMilliseconds(value: unknown, fallback: number): string {
  const milliseconds = typeof value === "number" && Number.isFinite(value) ? value : fallback;
  return new Date(milliseconds).toISOString();
}

function isMcpLikeTool(toolName: string): boolean {
  return toolName.startsWith("mcp__") || toolName.startsWith("orgx_");
}

function readOpenCodeStore(
  database: SqliteDatabase,
  options: { fallbackMtimeMs: number; limit: number; sinceMs: number },
): AiClientSqliteStoreResult {
  const sessions = database.prepare(`
    SELECT id, time_created, time_updated
    FROM session
    WHERE time_updated >= ?
    ORDER BY time_updated DESC
    LIMIT ?
  `).all(options.sinceMs, options.limit);
  const events: AiClientSqliteEvent[] = [];
  let reasoningPartsOmitted = 0;

  const partsForSession = database.prepare(`
    SELECT
      m.id AS message_id,
      m.time_created AS message_created,
      m.data AS message_data,
      p.id AS part_id,
      p.time_created AS part_created,
      p.data AS part_data
    FROM message AS m
    LEFT JOIN part AS p ON p.message_id = m.id
    WHERE m.session_id = ?
    ORDER BY m.time_created ASC, p.time_created ASC, p.id ASC
    LIMIT 2000
  `);

  for (const session of sessions) {
    if (typeof session.id !== "string") continue;
    const fallback = typeof session.time_updated === "number"
      ? session.time_updated
      : options.fallbackMtimeMs;
    for (const row of partsForSession.all(session.id)) {
      const message = parseObject(row.message_data);
      const role = message?.role === "user" || message?.role === "assistant"
        ? message.role
        : "meta";
      const part = parseObject(row.part_data);
      if (!part) continue;
      const type = textField(part, "type");
      const timestamp = isoFromMilliseconds(row.part_created ?? row.message_created, fallback);
      const rawRowId = `${String(row.message_id ?? "message")}:${String(row.part_id ?? "part")}`;

      if (type === "reasoning") {
        reasoningPartsOmitted += 1;
        continue;
      }
      if (type === "text") {
        const text = textField(part, "text");
        if (!text || (role !== "user" && role !== "assistant")) continue;
        events.push({
          kind: role === "user" ? "user_prompt" : "assistant_text",
          rawRowId,
          role,
          sessionId: session.id,
          text,
          timestamp,
        });
        continue;
      }
      if (type !== "tool") continue;

      const toolName = textField(part, "tool") ?? "unknown_tool";
      const state = isRecord(part.state) ? part.state : {};
      const status = textField(state, "status") ?? "unknown";
      const error = textField(state, "error");
      events.push({
        kind: status === "error"
          ? "tool_call_error"
          : isMcpLikeTool(toolName)
            ? "mcp_tool_call"
            : "tool_call_start",
        rawRowId,
        role: "tool",
        sessionId: session.id,
        text: error ? `Tool ${toolName} failed: ${error}` : `Tool ${toolName} ${status}`,
        timestamp,
        toolName,
        toolStatus: status,
      });
    }
  }

  return {
    events,
    notes: [
      "OpenCode SQLite was queried read-only with bounded session and row limits.",
      ...(reasoningPartsOmitted > 0
        ? [`Omitted ${reasoningPartsOmitted} private reasoning parts; final text and tool state were retained.`]
        : []),
    ],
    readable: true,
    searchedSessions: sessions.length,
  };
}

function cursorToolResultFailed(result: unknown): boolean {
  if (isRecord(result)) {
    return result.isError === true || result.success === false || typeof result.error === "string";
  }
  return typeof result === "string" && /^(?:error|failed|exception)\b/i.test(result.trim());
}

function readCursorStore(
  database: SqliteDatabase,
  path: string,
  options: { fallbackMtimeMs: number; sinceMs: number },
): AiClientSqliteStoreResult {
  const sessionId = basename(dirname(path));
  const metaPath = join(dirname(path), "meta.json");
  let meta: Record<string, unknown> = {};
  if (existsSync(metaPath)) {
    meta = parseObject(readFileSync(metaPath, "utf8")) ?? {};
  }
  const sessionUpdated = typeof meta.updatedAtMs === "number"
    ? meta.updatedAtMs
    : options.fallbackMtimeMs;
  if (sessionUpdated < options.sinceMs) {
    return { events: [], notes: [], readable: true, searchedSessions: 0 };
  }

  const rows = database.prepare(`
    SELECT rowid AS row_id, id, CAST(data AS TEXT) AS data
    FROM blobs
    WHERE json_valid(CAST(data AS TEXT)) = 1
    ORDER BY rowid ASC
    LIMIT 1200
  `).all();
  const events: AiClientSqliteEvent[] = [];
  let reasoningPartsOmitted = 0;

  for (const row of rows) {
    const message = parseObject(row.data);
    if (!message || (message.role !== "user" && message.role !== "assistant")) continue;
    const role = message.role;
    const content = message.content;
    const baseRowId = `${String(row.row_id ?? "row")}:${String(row.id ?? "blob")}`;

    if (typeof content === "string" && content.trim()) {
      events.push({
        kind: role === "user" ? "user_prompt" : "assistant_text",
        rawRowId: baseRowId,
        role,
        sessionId,
        text: content,
        timestamp: isoFromMilliseconds(meta.updatedAtMs, sessionUpdated),
      });
      continue;
    }
    if (!Array.isArray(content)) continue;

    for (const [index, part] of content.entries()) {
      if (!isRecord(part)) continue;
      const type = textField(part, "type");
      const rawRowId = `${baseRowId}:${index}`;
      const timestamp = isoFromMilliseconds(meta.updatedAtMs, sessionUpdated);
      if (type === "reasoning") {
        reasoningPartsOmitted += 1;
        continue;
      }
      if (type === "text") {
        const text = textField(part, "text");
        if (!text) continue;
        events.push({
          kind: role === "user" ? "user_prompt" : "assistant_text",
          rawRowId,
          role,
          sessionId,
          text,
          timestamp,
        });
        continue;
      }
      if (type !== "tool-call" && type !== "tool-result") continue;

      const toolName = textField(part, "toolName") ?? "unknown_tool";
      const failed = type === "tool-result" && cursorToolResultFailed(part.result);
      events.push({
        kind: failed
          ? "tool_call_error"
          : type === "tool-result"
            ? "tool_call_result"
            : isMcpLikeTool(toolName)
              ? "mcp_tool_call"
              : "tool_call_start",
        rawRowId,
        role: "tool",
        sessionId,
        text: `Tool ${toolName} ${failed ? "failed" : type === "tool-call" ? "called" : "completed"}`,
        timestamp,
        toolName,
        toolStatus: failed ? "error" : type === "tool-result" ? "completed" : "called",
      });
    }
  }

  return {
    events,
    notes: [
      "Cursor content-addressed chat storage was queried read-only; system blobs and provider metadata were excluded.",
      "Cursor chat blobs expose session-level timestamps, so event ordering is exact within the store while event time is session-level.",
      ...(reasoningPartsOmitted > 0
        ? [`Omitted ${reasoningPartsOmitted} private reasoning parts; final text and tool state were retained.`]
        : []),
    ],
    readable: true,
    searchedSessions: 1,
  };
}

export function readAiClientSqliteStore(
  source: "opencode" | "cursor" | "goose",
  path: string,
  options: { limit: number; sinceMs: number },
): AiClientSqliteStoreResult {
  const module = sqliteModule();
  if (!module) {
    return {
      events: [],
      notes: ["This Node runtime does not provide node:sqlite; the detected store was left unread."],
      readable: false,
      searchedSessions: 0,
    };
  }
  const stats = statSync(path);
  let database: SqliteDatabase;
  try {
    database = new module.DatabaseSync(path, { readOnly: true });
  } catch {
    return {
      events: [],
      notes: ["The detected SQLite store could not be opened read-only."],
      readable: false,
      searchedSessions: 0,
    };
  }

  try {
    const tables = new Set(
      database.prepare("SELECT name FROM sqlite_master WHERE type = 'table'").all()
        .map((row) => typeof row.name === "string" ? row.name : "")
        .filter(Boolean),
    );
    if (source === "opencode" && ["session", "message", "part"].every((table) => tables.has(table))) {
      return readOpenCodeStore(database, {
        fallbackMtimeMs: stats.mtimeMs,
        limit: options.limit,
        sinceMs: options.sinceMs,
      });
    }
    if (source === "cursor" && tables.has("blobs") && tables.has("meta")) {
      return readCursorStore(database, path, {
        fallbackMtimeMs: stats.mtimeMs,
        sinceMs: options.sinceMs,
      });
    }
    return {
      events: [],
      notes: [`Detected ${source} SQLite schema is not supported by the local reader.`],
      readable: false,
      searchedSessions: 0,
    };
  } finally {
    database.close();
  }
}
